import { FC } from 'react';
import { createPortal } from 'react-dom';

import { X } from 'lucide-react';

import { Button } from '@/components/TailwindComponents';

interface UnsavedChangesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCancel?: () => void;
  onConfirm: () => void;
  title?: string;
  description?: string;
  confirmText?: string;
  cancelText?: string;
  isLoading?: boolean;
}

export const UnsavedChangesModal: FC<UnsavedChangesModalProps> = ({
  isOpen,
  onClose,
  onCancel,
  onConfirm,
  title = 'You have unsaved changes',
  description = 'Please save your changes before leaving this page.',
  confirmText = 'Save and continue',
  cancelText = 'Back',
  isLoading = false,
}) => {
  if (!isOpen) return null;

  const handleCancel = onCancel ?? onClose;

  return createPortal(
    <div className="fixed inset-0 z-[1400] flex items-center justify-center bg-black bg-opacity-50">
      <div className="mx-4 w-full max-w-md rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b p-4">
          <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-xl text-gray-400 hover:text-gray-600"
            disabled={isLoading}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-4">
          <p className="text-sm text-gray-600">{description}</p>
        </div>

        <div className="flex justify-end space-x-2 border-t p-4">
          <Button
            type="button"
            variant="outline"
            onClick={handleCancel}
            disabled={isLoading}
          >
            {cancelText}
          </Button>
          <Button type="button" onClick={onConfirm} disabled={isLoading}>
            {isLoading ? (
              <div className="flex items-center space-x-2">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                <span>Loading...</span>
              </div>
            ) : (
              confirmText
            )}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
};
